'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { API_ROUTES, useApiMutation, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { useSeonSession } from '@shared/hooks';
import { useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import { useLatestTransactionSummary } from '../../../my-account/Wallet/hooks/useLatestTransactionSummary';
import { useWalletBalance } from '../../../my-account/Wallet/hooks/useWalletBalance';
import { useGlobalSheet } from '../../context/GlobalSheetContext';
import { PaymentsSheet } from '../PaymentsSheet/PaymentsSheet';
import { WithdrawAmountStep, WithdrawBankDetailsStep, WithdrawErrorStep, WithdrawSuccessStep } from './components';
import { useCreatePayout } from './hooks/useCreatePayout';
import type { CreateBankInstrumentRequest, CreatePayoutRequest, PayoutInstrumentsResponse, PayoutsInstrumentBank } from './types';
import { createWithdrawalSchema } from './withdrawalSchema';

const debug = createDebug({ isEnabled: false });

type WithdrawalFormData = {
  amountStep: {
    amount: string;
    paymentMethod: string;
  };
  methodStep: {
    accountNumber: string;
    bankCode: string;
    bank?: string;
    accountName: string;
  };
};
function getDefaultWithdrawalValues(accountHolderName = ''): WithdrawalFormData {
  return {
    amountStep: {
      amount: '',
      paymentMethod: '',
    },
    methodStep: {
      accountNumber: '',
      bankCode: '',
      bank: '',
      accountName: accountHolderName,
    },
  };
}

function WithdrawSheet() {
  const t = useTranslations('global_sheets.withdraw');
  const { session } = useSession();
  const accountHolderNameFromSession = session?.profile ? [session.profile.firstName, session.profile.lastName].filter(Boolean).join(' ').trim() : '';
  const { isWithdrawSheetOpen, closeWithdrawSheet, withdrawSheetTitle, resetWithdrawSheetTitle } = useGlobalSheet();
  const formatCurrency = useCurrencyFormatter();
  const playerId = session?.profile?.id;
  const [withdrawErrorMessage, setWithdrawErrorMessage] = useState<string | null>(null);
  const [isSubmittingBankDetails, setIsSubmittingBankDetails] = useState(false);
  const isSubmittingRef = useRef(false);
  const trackingRef = useRef<{ method?: string; amount?: string; transactionId?: string; provider?: string } | null>(null);
  const collectBankName = getSiteConfig().WITHDRAWAL.bankFields.includes('bankName');

  // Fetch wallet data to get available balance
  const { accountSummary } = useLatestTransactionSummary();

  const { withdrawableBalance: walletFrontofficeWithdrawableBalance } = useWalletBalance(isWithdrawSheetOpen);

  const withdrawableBalance = walletFrontofficeWithdrawableBalance ?? accountSummary?.withdrawableBalance ?? 0;

  const { data: cashierInfo, refetch: refetchCashierInfo } = useApiQuery<PayoutInstrumentsResponse>(API_ROUTES.PAYMENTS.PAYOUTS.GET_CASHIER_INFO, {
    queryOptions: {
      enabled: isWithdrawSheetOpen,
    },
    credentials: true,
  });

  const minWithdrawalAmount = cashierInfo?.minPayoutAmount ?? 0;

  // Create schema with formatted balance message
  const withdrawalSchema = useMemo(
    () => createWithdrawalSchema({ t, formatCurrency, withdrawableBalance, minWithdrawalAmount }),
    [withdrawableBalance, minWithdrawalAmount, t, formatCurrency]
  );

  const form = useForm<WithdrawalFormData>({
    defaultValues: getDefaultWithdrawalValues(),
    resolver: zodResolver(withdrawalSchema),
  });

  const { watch, trigger, clearErrors, setValue, getValues } = form;

  const resetForm = useCallback(() => {
    form.reset(getDefaultWithdrawalValues(accountHolderNameFromSession));
    setWithdrawErrorMessage(null);
    trackingRef.current = null;
  }, [form, accountHolderNameFromSession]);
  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const setStatusRef = useRef<((status: string) => void) | null>(null);
  const [trackContainer, setTrackContainer] = useState<string>('Amount');

  const handleBackFromAccountDetails = useCallback(() => {
    if (setStatusRef.current) {
      setStatusRef.current('');
    }
    if (skipToStepRef.current) {
      skipToStepRef.current('amount');
    }
    setTrackContainer('Amount');
    resetWithdrawSheetTitle();
  }, [resetWithdrawSheetTitle]);

  const amount = watch('amountStep.amount');

  // Clear amount validation errors when the balance or the cashier minimum changes, since any shown error came
  // from a schema built with the previous values
  useEffect(() => {
    if (withdrawableBalance > 0) {
      clearErrors('amountStep.amount');
    }
  }, [withdrawableBalance, minWithdrawalAmount, clearErrors]);

  // Set account holder name from session to form. When clicking back, don't overwrite the name if it's already set
  useEffect(() => {
    if (!accountHolderNameFromSession) return;

    const currentAccountName = getValues('methodStep.accountName');
    if (currentAccountName?.trim()) return;

    setValue('methodStep.accountName', accountHolderNameFromSession, { shouldDirty: false, shouldValidate: true });
  }, [accountHolderNameFromSession, getValues, setValue]);

  // API Mutations
  const createBankInstrumentMutation = useApiMutation<PayoutsInstrumentBank, CreateBankInstrumentRequest>(API_ROUTES.PAYMENTS.PAYOUTS.CREATE_BANK_INSTRUMENT, 'POST', {
    requestOptions: { credentials: true },
  });

  const createPayoutMutation = useCreatePayout();
  const { getSeonSessionId } = useSeonSession({ preload: isWithdrawSheetOpen });

  const handleBankDetails = useCallback(async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      const isValid = await trigger('methodStep');
      if (!isValid) return;

      setIsSubmittingBankDetails(true);

      const formValues = form.getValues();

      // Set tracking details
      trackingRef.current = {
        method: 'Bank Transfer',
        amount: formValues.amountStep.amount,
      };

      // Step 1: Create bank instrument.
      const bankInstrumentData: CreateBankInstrumentRequest = {
        bankCode: formValues.methodStep.bankCode || '',
        holder: formValues.methodStep.accountName || '',
        number: formValues.methodStep.accountNumber || '',
      };

      if (collectBankName) {
        bankInstrumentData.bankName = formValues.methodStep.bank;
      }

      const instrumentResponse = await createBankInstrumentMutation.mutateAsync(bankInstrumentData);

      if (!instrumentResponse) {
        throw new Error('Failed to create bank instrument');
      }

      // Step 2: Get cashier info
      if (typeof refetchCashierInfo === 'function') {
        await refetchCashierInfo();
      }

      // Step 3: Create payout
      const seonSessionId = await getSeonSessionId();
      const payoutData: CreatePayoutRequest = {
        apiReference: instrumentResponse.apiReference,
        amount: Number(formValues.amountStep.amount) * 100,
        documentId: playerId || '',
        ...(seonSessionId && { seonSessionId }),
      };

      const payoutResponse = await createPayoutMutation.mutateAsync(payoutData);

      // Update tracking with transaction ID if available
      if (payoutResponse) {
        trackingRef.current = {
          ...trackingRef.current,
          transactionId: payoutResponse.reference || payoutResponse.payoutId,
        };
      }

      // Navigate to success step
      if (setStatusRef.current) {
        setStatusRef.current('success');
      }
      if (skipToStepRef.current) {
        skipToStepRef.current('success');
      }
    } catch (error: unknown) {
      debug.forceError('Withdrawal flow error:', error);

      const errorObj = error as Record<string, unknown>;

      // Handle specific bank instrument errors
      if (errorObj?.errorKey === 'aurumErrors_invalidInstrumentHolder') {
        form.setError('methodStep.accountName', {
          type: 'server',
          message: t('errors.invalid_account_holder'),
        });
        return;
      }

      // Handle general errors - navigate to error step
      const errorMessage = (errorObj?.message as string) || t('errors.unexpected');
      setWithdrawErrorMessage(errorMessage);

      if (setStatusRef.current) {
        setStatusRef.current('error');
      }
      if (skipToStepRef.current) {
        skipToStepRef.current('error');
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmittingBankDetails(false);
    }
  }, [trigger, form, createBankInstrumentMutation, refetchCashierInfo, createPayoutMutation, getSeonSessionId, playerId, collectBankName, t]);

  const getSteps = useMemo(
    () => [
      {
        id: 'amount',
        component: (
          <WithdrawAmountStep
            namespace="global_sheets.withdraw"
            withdrawableBalance={withdrawableBalance}
            onError={error => {
              debug.forceError('Amount step error:', error);
              setWithdrawErrorMessage(error);
            }}
            onMount={() => setTrackContainer('Amount')}
            setTrackingDetails={details => {
              trackingRef.current = details;
            }}
            data-track-container="Amount"
            data-track-type="Withdrawal"
            data-track-count="1"
          />
        ),
        schema: withdrawalSchema.shape.amountStep,
      },
      {
        id: 'method_details',
        component: (
          <WithdrawBankDetailsStep
            amount={amount}
            namespace="global_sheets.withdraw"
            onBankDetailsSubmit={handleBankDetails}
            isSubmitting={isSubmittingBankDetails}
            onMount={() => setTrackContainer('Account Details')}
            data-track-container="Account Details"
            data-track-type="Withdrawal"
            data-track-count="1"
          />
        ),
        schema: withdrawalSchema.shape.methodStep,
      },
      {
        id: 'error',
        component: (
          <WithdrawErrorStep
            namespace="global_sheets.withdraw"
            onMount={() => setTrackContainer('Withdraw Requested')}
            data-track-container="Withdraw Requested"
            data-track-category="❌❌❌"
            data-track-type="Withdrawal"
            data-track-count="1"
            data-track-error={withdrawErrorMessage || ''}
            {...(trackingRef.current?.method && { 'data-track-method': trackingRef.current.method })}
            {...(trackingRef.current?.amount && { 'data-track-amount': trackingRef.current.amount })}
            {...(trackingRef.current?.transactionId && { 'data-track-id': trackingRef.current.transactionId })}
          />
        ),
        schema: z.any(),
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'error';
        },
      },
      {
        id: 'success',
        component: (
          <WithdrawSuccessStep
            namespace="global_sheets.withdraw"
            onMount={() => setTrackContainer('Withdraw Requested')}
            data-track-container="Withdraw Requested"
            data-track-type="Withdrawal"
            data-track-count="1"
            data-track-status="Requested"
            {...(trackingRef.current?.method && { 'data-track-method': trackingRef.current.method })}
            {...(trackingRef.current?.amount && { 'data-track-amount': trackingRef.current.amount })}
            {...(trackingRef.current?.provider && { 'data-track-provider': trackingRef.current.provider })}
          />
        ),
        schema: z.any(),
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'success';
        },
      },
    ],
    [withdrawalSchema.shape.amountStep, withdrawalSchema.shape.methodStep, amount, handleBankDetails, isSubmittingBankDetails, withdrawableBalance, withdrawErrorMessage]
  );

  return (
    <PaymentsSheet
      title={withdrawSheetTitle}
      showBackButton={trackContainer === 'Account Details' && !isSubmittingBankDetails}
      onBack={handleBackFromAccountDetails}
      data-track-feature="Withdraw"
      data-track-container={trackContainer}
      open={isWithdrawSheetOpen}
      onOpenChange={open => {
        if (!open) {
          resetForm();
          resetWithdrawSheetTitle();
          closeWithdrawSheet();
          return;
        }

        form.reset(getDefaultWithdrawalValues(accountHolderNameFromSession));
      }}
    >
      <FormProvider {...form}>
        <MultistepForm
          steps={getSteps}
          showProgressBar={false}
          showNavigation={false}
          onComplete={async () => {}}
          onMultistepFormReady={(skipToStep, setStatus) => {
            skipToStepRef.current = skipToStep;
            setStatusRef.current = setStatus;
          }}
          className="h-full px-6 py-8"
        />
      </FormProvider>
    </PaymentsSheet>
  );
}

export { WithdrawSheet };
export type { WithdrawalFormData };
